import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const ArticleList = ({
  articles,
  searchQuery,
  selectedCategory,
  filterDifficulty,
  filterContentType,
  sortBy,
  onArticleSelect
}) => {
  const filteredArticles = useMemo(() => {
    let filtered = articles;

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(a =>
        a?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        a?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        a?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered?.filter(a => a?.categoryId === selectedCategory);
    }

    // Difficulty filter
    if (filterDifficulty !== 'all') {
      filtered = filtered?.filter(a => a?.difficulty?.toLowerCase() === filterDifficulty);
    }

    // Content type filter
    if (filterContentType !== 'all') {
      filtered = filtered?.filter(a => a?.contentType?.toLowerCase() === filterContentType);
    }

    // Sort
    filtered = [...filtered]?.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b?.views - a?.views;
        case 'recent':
          return new Date(b?.lastUpdated) - new Date(a?.lastUpdated);
        case 'rating':
          return b?.rating - a?.rating;
        default:
          return 0;
      }
    });

    return filtered;
  }, [articles, searchQuery, selectedCategory, filterDifficulty, filterContentType, sortBy]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-500/10 text-green-500';
      case 'Intermediate':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'Advanced':
        return 'bg-red-500/10 text-red-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (filteredArticles?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No articles found</h3>
        <p className="text-sm text-muted-foreground">
          Try adjusting your search or filters
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {filteredArticles?.length} {filteredArticles?.length === 1 ? 'article' : 'articles'} found
        </p>
      </div>

      {filteredArticles?.map((article) => (
        <button
          key={article?.id}
          onClick={() => onArticleSelect(article)}
          className="w-full bg-card border border-border rounded-lg p-5 hover:shadow-elevation-md hover:border-primary/50 transition-smooth text-left group"
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${getDifficultyColor(article?.difficulty)}`}>
                {article?.difficulty}
              </span>
              <span className="px-2.5 py-1 bg-muted text-muted-foreground rounded-md text-xs font-medium">
                {article?.contentType}
              </span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Icon name="Star" size={14} className="text-yellow-500 fill-yellow-500" />
              <span className="text-xs font-medium">{article?.rating}</span>
            </div>
          </div>

          <h3 className="text-lg font-heading font-semibold text-foreground mb-2 group-hover:text-primary transition-smooth">
            {article?.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {article?.description}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Eye" size={12} />
                <span>{article?.views?.toLocaleString()} views</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={12} />
                <span>{article?.readTime}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={12} />
                <span>Updated {new Date(article?.lastUpdated)?.toLocaleDateString()}</span>
              </div>
            </div>
            <span className="text-xs text-primary font-medium">{article?.category}</span>
          </div>

          {/* Tags */}
          {article?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
              {article?.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </button>
      ))}
    </div>
  );
};

export default ArticleList;